import { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STORAGE_KEY = 'billlens.selectedMonth';

export function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Returns the last month the user picked (persisted in localStorage), or
 * the current calendar month on first visit. Used as the initial value for
 * `useState` in every page that has a MonthPicker so the selection sticks
 * across page navigation.
 */
export function persistedMonth() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v && (v === 'all' || /^\d{4}-\d{2}$/.test(v))) return v;
    } catch {}
    return currentMonth();
}

/**
 * useSharedMonth() — a tiny hook that gives every page a month selector
 * synced with localStorage. Changes in one page propagate to others when
 * they next mount.
 */
export function useSharedMonth() {
    const [month, setMonthState] = useState(() => persistedMonth());
    // Also react to changes made elsewhere in the same tab (custom event) or
    // in other tabs (storage event).
    useEffect(() => {
        const handler = () => setMonthState(persistedMonth());
        window.addEventListener('storage', handler);
        window.addEventListener('billlens:monthchange', handler);
        return () => {
            window.removeEventListener('storage', handler);
            window.removeEventListener('billlens:monthchange', handler);
        };
    }, []);
    const setMonth = (v) => {
        try { localStorage.setItem(STORAGE_KEY, v); } catch {}
        setMonthState(v);
        // Notify other MonthPickers on the same page in the same tick.
        try { window.dispatchEvent(new Event('billlens:monthchange')); } catch {}
    };
    return [month, setMonth];
}

export function labelForMonth(ym) {
    if (!ym || ym === 'all') return 'All time';
    try {
        const [y, m] = ym.split('-').map((x) => Number(x));
        const d = new Date(Date.UTC(y, (m || 1) - 1, 1));
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } catch {
        return ym;
    }
}

/**
 * MonthPicker — a Shadcn Select that lets the user pick a month or "All time".
 * Generates 24 months back from today (past 2 years) plus the current month.
 * Usage: <MonthPicker value={month} onChange={setMonth} />
 */
export function MonthPicker({ value, onChange, includeAllTime = true, className, testid = 'month-picker' }) {
    const options = useMemo(() => {
        const opts = [];
        if (includeAllTime) opts.push({ value: 'all', label: 'All time' });
        const now = new Date();
        for (let i = 0; i < 24; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label =
                i === 0 ? `This month · ${labelForMonth(ym)}` : labelForMonth(ym);
            opts.push({ value: ym, label });
        }
        return opts;
    }, [includeAllTime]);

    return (
        <div className={className}>
            <Select value={value || 'all'} onValueChange={onChange}>
                <SelectTrigger data-testid={testid} className="min-w-[200px] gap-2">
                    <Calendar size={14} className="text-muted-foreground" />
                    <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[320px] overflow-auto">
                    {options.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`${testid}-opt-${o.value}`}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
